import type { RequesterIdentity } from "../entitlements-api";

export interface RequesterLabel {
  /** What to show. Never empty: an operator needs something to act on. */
  readonly text: string;
  /**
   * True when `text` is a bare id rather than a name the service resolved, so
   * the surface can say the requester is unresolved instead of presenting a
   * UUID as though it were a person.
   */
  readonly unresolved: boolean;
}

/**
 * How a requester is named on screen (ENR-2246).
 *
 * Name, or the email where no name is set, or the id where the lookup found
 * nobody. The id is deliberately still shown in that last case rather than a
 * blank or a dash: the row stays workable, and an operator chasing an
 * unresolvable requester in a support thread has the only handle that exists.
 *
 * A blank or whitespace-only display name is treated as no name at all. It
 * reaches here as a non-null string, so trusting it would render a row labelled
 * with nothing and read as a bug in this console.
 */
export function requesterLabel(requester: RequesterIdentity, requesterId: string): RequesterLabel {
  if (!requester.resolved) {
    return { text: requesterId, unresolved: true };
  }
  const name = requester.displayName?.trim() ?? "";
  if (name !== "") {
    return { text: name, unresolved: false };
  }
  const email = requester.email?.trim() ?? "";
  return email === ""
    ? { text: requesterId, unresolved: true }
    : { text: email, unresolved: false };
}
